import React, { useEffect, useState } from 'react';
import { Button, Card, List, Progress, Space, Tooltip, Typography } from 'antd';
import { CloseOutlined, DownOutlined, ReloadOutlined, UpOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { fmtBytes } from './common';
import { cancelUpload, clearFinished, retryUpload, subscribe } from './upload';

const { Text } = Typography;

/** Floating object-storage upload list, mounted once for the whole console (uploads survive page changes). */
const UploadPanel = () => {
    const { t } = useTranslation();
    const [jobs, setJobs] = useState([]);
    const [open, setOpen] = useState(true);
    useEffect(() => subscribe(setJobs), []);

    const visible = jobs.filter((j) => j.status !== 'canceled');
    if (!visible.length) return null;
    const active = visible.filter((j) => j.status === 'queued' || j.status === 'uploading');
    const total = active.reduce((a, j) => a + j.size, 0);
    const done = active.reduce((a, j) => a + (j.loaded || 0), 0);
    const failed = visible.filter((j) => j.status === 'error').length;
    const title = active.length
        ? t('Uploading {{count}} file(s) · {{percent}}%', { count: active.length, percent: total ? Math.floor((done / total) * 100) : 0 })
        : failed ? t('{{count}} upload(s) failed', { count: failed }) : t('Uploads complete');

    return (
        <Card size="small"
            style={{ position: 'fixed', right: 16, bottom: 16, width: 380, maxWidth: 'calc(100vw - 32px)', zIndex: 1000, boxShadow: '0 6px 24px rgba(0,0,0,0.15)' }}
            title={<Space size={6}><CloudUploadOutlined />{title}</Space>}
            extra={(
                <Space size={0}>
                    <Button size="small" type="text" icon={open ? <DownOutlined /> : <UpOutlined />} onClick={() => setOpen(!open)} />
                    {!active.length && <Button size="small" type="text" icon={<CloseOutlined />} onClick={clearFinished} />}
                </Space>
            )}
            styles={{ body: { padding: open ? '4px 12px' : 0, display: open ? 'block' : 'none' } }}>
            <List size="small" dataSource={visible} style={{ maxHeight: 300, overflow: 'auto' }}
                renderItem={(j) => {
                    const pct = j.size ? Math.min(100, Math.floor(((j.loaded || 0) / j.size) * 100)) : (j.status === 'done' ? 100 : 0);
                    return (
                        <List.Item style={{ padding: '6px 0' }} actions={[
                            j.status === 'error' && <Tooltip key="r" title={t('Retry')}><Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => retryUpload(j.uid)} /></Tooltip>,
                            (j.status === 'queued' || j.status === 'uploading') && <Button key="c" size="small" type="text" icon={<CloseOutlined />} onClick={() => cancelUpload(j.uid)} />,
                        ].filter(Boolean)}>
                            <div style={{ width: '100%', minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                    <Tooltip title={`${j.bucketName}/${j.key}`}><Text ellipsis style={{ maxWidth: 210 }}>{j.key.split('/').pop()}</Text></Tooltip>
                                    <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {fmtBytes(j.size)}{j.parts ? ` · ${j.partsDone || 0}/${j.parts}` : ''}
                                    </Text>
                                </div>
                                {j.status === 'error'
                                    ? <Text type="danger" style={{ fontSize: 12 }}>{j.error || t('Upload failed')}</Text>
                                    : <Progress size="small" percent={pct} style={{ margin: 0 }}
                                        status={j.status === 'done' ? 'success' : j.status === 'uploading' ? 'active' : 'normal'} />}
                                {j.retrying && <Text type="warning" style={{ fontSize: 12 }}>{t('Connection lost, retrying…')}</Text>}
                                {j.resumed && j.status === 'uploading' && <Text type="secondary" style={{ fontSize: 12 }}>{t('Resumed from where it stopped')}</Text>}
                            </div>
                        </List.Item>
                    );
                }} />
        </Card>
    );
};

export default UploadPanel;
